import { createContext, useContext, useState, ReactNode } from 'react';
import { SalesFunnel } from '../types';

interface FunnelContextType {
  selectedFunnelId: number | null;
  setSelectedFunnelId: (id: number | null) => void;
  funnels: SalesFunnel[];
  setFunnels: (funnels: SalesFunnel[]) => void;
  selectedFunnel: SalesFunnel | null;
}

const FunnelContext = createContext<FunnelContextType | undefined>(undefined);

const STORAGE_KEY = 'crm-selected-funnel-id';

export function FunnelProvider({ children }: { children: ReactNode }) {
  const [selectedFunnelId, setSelectedFunnelIdState] = useState<number | null>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? Number.parseInt(stored, 10) : null;
    }
    return null;
  });
  const [funnels, setFunnels] = useState<SalesFunnel[]>([]);

  const setSelectedFunnelId = (id: number | null) => {
    setSelectedFunnelIdState(id);
    if (typeof window !== 'undefined') {
      if (id !== null) {
        localStorage.setItem(STORAGE_KEY, id.toString());
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  };

  // Compute selectedFunnel - this will automatically update when funnels or selectedFunnelId changes
  const selectedFunnel = funnels.length > 0 && selectedFunnelId
    ? funnels.find(f => f.id === selectedFunnelId) || null
    : funnels.find(f => f.is_default) || funnels[0] || null;

  return (
    <FunnelContext.Provider
      value={{
        selectedFunnelId,
        setSelectedFunnelId,
        funnels,
        setFunnels,
        selectedFunnel,
      }}
    >
      {children}
    </FunnelContext.Provider>
  );
}

export function useFunnelContext() {
  const context = useContext(FunnelContext);
  if (context === undefined) {
    throw new Error('useFunnelContext must be used within a FunnelProvider');
  }
  return context;
}

